// Pure pub/sub singleton (same pattern as UmlCanvasEngine/UmlViewportEngine) —
// tracks which single shape is currently "selected" (click-to-select, distinct
// from hover-glow and from an active drag). No React knowledge here.
class UmlSelectionEngine {
    constructor() {
        this.selectedId = null;
        this.selectedShapeIds = new Set(); // shape-only multi-selection, kept separate from selectedId (also used for connectors/anchors)
        this.listeners = [];
    }

    subscribe(callback) {
        this.listeners.push(callback);
        return function unsubscribe() {
            this.listeners = this.listeners.filter(function filterOutListener(listener) {
                return listener !== callback;
            });
        }.bind(this);
    }

    notify() {
        this.listeners.forEach(function callListener(listener) {
            listener();
        });
    }

    select(shapeId) {
        // Keeps an existing multi-selection intact only if shapeId is already part of
        // it — lets a plain mousedown start a group-drag without collapsing the group.
        const keepGroup = this.selectedShapeIds.size > 1 && this.selectedShapeIds.has(shapeId);
        let changed = false;
        if (!keepGroup && this.selectedShapeIds.size > 0) {
            this.selectedShapeIds.clear();
            changed = true;
        }
        if (this.selectedId !== shapeId) {
            this.selectedId = shapeId;
            changed = true;
        }
        if (changed) this.notify();
    }

    /**
     * Shift+click — toggles a shape's membership in the multi-selection.
     * Seeds the group with the currently single-selected shape on the first toggle.
     * @param {string} shapeId
     */
    toggleShapeSelection(shapeId) {
        if (this.selectedShapeIds.has(shapeId)) {
            this.selectedShapeIds.delete(shapeId);
            if (this.selectedId === shapeId) {
                // fall back to another group member so isSelected() (selectedId ||
                // selectedShapeIds.has) doesn't keep highlighting the removed shape
                const remaining = this.selectedShapeIds.values().next().value;
                this.selectedId = remaining ?? null;
            }
            this.notify();
            return;
        }
        if (this.selectedShapeIds.size === 0 && this.selectedId) {
            this.selectedShapeIds.add(this.selectedId);
        }
        this.selectedShapeIds.add(shapeId);
        this.selectedId = shapeId;
        this.notify();
    }

    clear() {
        if (this.selectedId === null && this.selectedShapeIds.size === 0) {
            return;
        }
        this.selectedId = null;
        this.selectedShapeIds.clear();
        this.notify();
    }

    isSelected(shapeId) {
        return this.selectedId === shapeId;
    }
}

const umlSelectionEngine = new UmlSelectionEngine();
export default umlSelectionEngine;
